<template>
  <b-card no-body v-if="items">
    <b-card-header>
      <h3 class="mb-0">Production Report</h3>
      <b-row class="mt-3">
        <b-col md="1">
          <base-input label="Active Members" label-for="active">
            <base-switch class="mr-1" v-model="active"></base-switch>
          </base-input>
        </b-col>
        <b-col md="3">
          <base-input label="Team Members" sr-only-label label-for="view" placeholder="Team Members" v-if="team.length > 0">
            <el-select v-model="member" placeholder="Select a Member">
              <el-option :value="null" label="Select a Member"></el-option>
              <el-option v-for="(member, index) in team"
                         :key="index"
                         :label="member.name"
                         :value="member.id">
              </el-option>
            </el-select>
          </base-input>
        </b-col>
        <b-col md="2">
          <production-report-views :fields="columns"></production-report-views>
        </b-col>
        <b-col md="6">
          <label class="form-control-label">Date Range</label>
          <b-input-group>
            <template #prepend>
              <b-dropdown variant="outline-danger">
                <template #button-content>
                  {{ rangeTitle }}
                </template>
                <b-dropdown-item v-for="item in ranges" :key="item.value" @click="range = item.value">{{ item.label }}</b-dropdown-item>
              </b-dropdown>
            </template>
            <b-form-datepicker v-model="start_date" locale="en" :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"></b-form-datepicker>
            <b-form-datepicker v-model="end_date" locale="en" :min="start_date" :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"></b-form-datepicker>
          </b-input-group>
        </b-col>
      </b-row>
    </b-card-header>
    <loading-panel v-if="loading"></loading-panel>
    <b-table id="production-report" responsive small :items="filteredItems" :fields="visibleColumns" no-sort-reset foot-clone v-else>
      <template #head(listings_sold)="row">
        Listings Pended
      </template>
      <template #head(listings_sold_volume)="row">
        Listings Pended Volume
      </template>
      <template #head(listings_sold_gci)="row">
        Listings Pended GCI
      </template>
      <template #head(buyers_sold)="row">
        Buyers Pended
      </template>
      <template #head(buyers_sold_volume)="row">
        Buyers Pended Volume
      </template>
      <template #head(buyers_sold_gci)="row">
        Buyers Pended GCI
      </template>
      <template #head(total_sales)="row">
        Total Pended
      </template>
      <template #head(total_sales_volume)="row">
        Total Pended Volume
      </template>
      <template #head(total_sales_gci)="row">
        Total Pended GCI
      </template>
      <template #cell(agents)="row">
          <b-avatar variant="primary" :text="initials(row.item.agents)" class="mr-2"></b-avatar> {{ row.item.agents }}
      </template>
      <template #cell(listings_taken_volume)="row">
        {{ row.item.listings_taken_volume | currency }}
      </template>
      <template #cell(listings_taken_gci)="row">
        {{ row.item.listings_taken_gci | currency }}
      </template>
      <template #cell(listings_sold_volume)="row">
        {{ row.item.listings_sold_volume | currency }}
      </template>
      <template #cell(listings_sold_gci)="row">
        {{ row.item.listings_sold_gci | currency }}
      </template>
      <template #cell(buyers_sold_volume)="row">
        {{ row.item.buyers_sold_volume | currency }}
      </template>
      <template #cell(buyers_sold_gci)="row">
        {{ row.item.buyers_sold_gci | currency }}
      </template>
      <template #cell(total_sales_volume)="row">
        {{ row.item.total_sales_volume | currency }}
      </template>
      <template #cell(total_sales_gci)="row">
        {{ row.item.total_sales_gci | currency }}
      </template>
      <template #cell(listings_closed_volume)="row">
        {{ row.item.listings_closed_volume | currency }}
      </template>
      <template #cell(listings_closed_gci)="row">
        {{ row.item.listings_closed_gci | currency }}
      </template>
      <template #cell(buyers_closed_volume)="row">
        {{ row.item.buyers_closed_volume | currency }}
      </template>
      <template #cell(buyers_closed_gci)="row">
        {{ row.item.buyers_closed_gci | currency }}
      </template>
      <template #cell(total_closed_volume)="row">
        {{ row.item.total_closed_volume | currency }}
      </template>
      <template #cell(total_closed_gci)="row">
        {{ row.item.total_closed_gci | currency }}
      </template>

      <!-- Footer -->
      <template #foot(agents)="row">
        Totals:
      </template>
      <template #foot(listings_taken)="row">
        {{ totalListingsTaken }}
      </template>
      <template #foot(listings_taken_volume)="row">
        {{ totalListingsTakenVolume | currency }}
      </template>
      <template #foot(listings_taken_gci)="row">
        {{ totalListingsTakenGci | currency }}
      </template>
      <template #foot(listings_sold)="row">
        {{ totalListingsSold }}
      </template>
      <template #foot(listings_sold_volume)="row">
        {{ totalListingsSoldVolume | currency }}
      </template>
      <template #foot(listings_sold_gci)="row">
        {{ totalListingsSoldGci | currency }}
      </template>
      <template #foot(buyers_sold)="row">
        {{ totalBuyersSold }}
      </template>
      <template #foot(buyers_sold_volume)="row">
        {{ totalBuyersSoldVolume | currency }}
      </template>
      <template #foot(buyers_sold_gci)="row">
        {{ totalBuyersSoldGci | currency }}
      </template>
      <template #foot(total_sales)="row">
        {{ totalSales }}
      </template>
      <template #foot(total_sales_volume)="row">
        {{ totalSalesVolume | currency }}
      </template>
      <template #foot(total_sales_gci)="row">
        {{ totalSalesGci | currency }}
      </template>
      <template #foot(listings_closed)="row">
        {{ totalListingsClosed }}
      </template>
      <template #foot(listings_closed_volume)="row">
        {{ totalListingsClosedVolume | currency }}
      </template>
      <template #foot(listings_closed_gci)="row">
        {{ totalListingsClosedGci | currency }}
      </template>
      <template #foot(buyers_closed)="row">
        {{ totalBuyersClosed }}
      </template>
      <template #foot(buyers_closed_volume)="row">
        {{ totalBuyersClosedVolume | currency }}
      </template>
      <template #foot(buyers_closed_gci)="row">
        {{ totalBuyersClosedGci | currency }}
      </template>
      <template #foot(total_closed)="row">
        {{ totalClosed }}
      </template>
      <template #foot(total_closed_volume)="row">
        {{ totalClosedVolume | currency }}
      </template>
      <template #foot(total_closed_gci)="row">
        {{ totalClosedGci | currency }}
      </template>
    </b-table>
  </b-card>
</template>

<script>
import axios from 'axios';
import columns from './productionColumns';
import ProductionReportViews from '@/views/Components/Tables/Views/ProductionReportViews';
import { Select, Option } from 'element-ui';
import LoadingPanel from '@/components/LoadingPanel';

export default {
  props: {
    userRoute: null
  },
  components: {
    ProductionReportViews,
    [Select.name]: Select,
    [Option.name]: Option,
    LoadingPanel
  },
  data() {
    return {
      items: null,
      columns: columns,
      start_date: new Date(new Date().getFullYear(), 0, 1),
      end_date: new Date(new Date().getFullYear(), 11, 31),
      range: 0,
      ranges: [
        { value: 0, label: "Current Year" },
        { value: 1, label: "Yesterday" },
        { value: 2, label: "Last 7 Days" },
        { value: 3, label: "Current Month" },
        { value: 4, label: "Last 3 Months" },
      ],
      viewBy: null,
      views: [
        { value: 'listings', label: "Listings Report" },
        { value: 'sales', label: "Sales Report" },
        { value: 'closings', label: "Closings Report" },
      ],
      team: [],
      active: true,
      member: null,
      loading: false,
    }
  },
  computed: {
    rangeTitle() {
      return this.ranges.filter((item) => {
        return item.value == this.range;
      })[0].label;
    },
    visibleColumns() {
      return this.columns.filter(field => field.visible)
    },
    totalListingsTaken() {
      return this.items.reduce((sum, item) => sum + item.listings_taken, 0);
    },
    totalListingsTakenVolume() {
      return this.items.reduce((sum, item) => sum + item.listings_taken_volume, 0);
    },
    totalListingsTakenGci() {
      return this.items.reduce((sum, item) => sum + item.listings_taken_gci, 0);
    },
    totalListingsSold() {
      return this.items.reduce((sum, item) => sum + item.listings_sold, 0);
    },
    totalListingsSoldVolume() {
      return this.items.reduce((sum, item) => sum + item.listings_sold_volume, 0);
    },
    totalListingsSoldGci() {
      return this.items.reduce((sum, item) => sum + item.listings_sold_gci, 0);
    },
    totalBuyersSold() {
      return this.items.reduce((sum, item) => sum + item.buyers_sold, 0);
    },
    totalBuyersSoldVolume() {
      return this.items.reduce((sum, item) => sum + item.buyers_sold_volume, 0);
    },
    totalBuyersSoldGci() {
      return this.items.reduce((sum, item) => sum + item.buyers_sold_gci, 0);
    },
    totalSales() {
      return this.items.reduce((sum, item) => sum + item.total_sales, 0);
    },
    totalSalesVolume() {
      return this.items.reduce((sum, item) => sum + item.total_sales_volume, 0);
    },
    totalSalesGci() {
      return this.items.reduce((sum, item) => sum + item.total_sales_gci, 0);
    },
    totalListingsClosed() {
      return this.items.reduce((sum, item) => sum + item.listings_closed, 0);
    },
    totalListingsClosedVolume() {
      return this.items.reduce((sum, item) => sum + item.listings_closed_volume, 0);
    },
    totalListingsClosedGci() {
      return this.items.reduce((sum, item) => sum + item.listings_closed_gci, 0);
    },
    totalBuyersClosed() {
      return this.items.reduce((sum, item) => sum + item.buyers_closed, 0);
    },
    totalBuyersClosedVolume() {
      return this.items.reduce((sum, item) => sum + item.buyers_closed_volume, 0);
    },
    totalBuyersClosedGci() {
      return this.items.reduce((sum, item) => sum + item.buyers_closed_gci, 0);
    },
    totalClosed() {
      return this.items.reduce((sum, item) => sum + item.total_closed, 0);
    },
    totalClosedVolume() {
      return this.items.reduce((sum, item) => sum + item.total_closed_volume, 0);
    },
    totalClosedGci() {
      return this.items.reduce((sum, item) => sum + item.total_closed_gci, 0);
    },
    filteredItems() {
      if (this.member) {
        return this.items.filter(item => {
          return item.agent_id == this.member;
        });
      } else {
        return this.items;
      }
    }
  },
  methods: {
    async getProductionReport() {
      this.loading = true;
      let startDate = new Date(this.start_date).toLocaleDateString("en-US");
      let endDate = new Date(this.end_date).toLocaleDateString("en-US");
      const active = this.active ? 1 : 0;

      axios.get(this.userRoute + '/reports/production-report?start_date=' + startDate + '&end_date=' + endDate + '&active=' + active)
      .then(response => {
        this.items = response.data;
        this.loading = false;
      })
      .catch(error => {

      });
    },
    updateRange() {
      switch(this.range) {
        case 0:
          this.start_date = new Date(new Date().getFullYear(), 0, 1);
          this.end_date = new Date(new Date().getFullYear(), 11, 31);
          break;
        case 1:
          var date = new Date();
          var start_date = new Date(date.setDate(date.getDate() - 1))
          this.start_date = start_date;
          this.end_date = start_date;
          break;
        case 2:
          var date = new Date();
          var start_date = new Date(date.setDate(date.getDate() - 7))
          this.start_date = start_date;
          this.end_date = new Date();
          break;
        case 3:
          var date = new Date();
          var start_date = new Date(date.setDate(date.getDate() - date.getDate() + 1))
          this.start_date = start_date;
          this.end_date = new Date();
          break;
        case 4:
          var date = new Date();
          var start_date = new Date(date.setDate(date.getDate() - 84))
          this.start_date = start_date;
          this.end_date = new Date();
          break;
        default:
          this.start_date = new Date(new Date().getFullYear(), 0, 1);
          this.end_date = new Date(new Date().getFullYear(), 11, 31);
      }
    },
    initials(agent) {
      let initials = agent.split(" ");

      if (initials.length > 1) {
        return initials[0].charAt(0) + initials[1].charAt(0);
      }

      return initials[0].charAt(0);
    },
    async getTeam() {
      const active = this.active ? 1 : 0;

      axios.get(this.userRoute + "/members/team?active=" + active)
      .then(response => {
        this.team = response.data;
      })
      .catch(error => {
        // errors
      });
    },
  },
  created() {
    this.getTeam();
    this.getProductionReport();
  },
  watch: {
    range(to, from) {
      this.updateRange();
    },
    start_date(to, from) {
      this.getProductionReport();
    },
    end_date(to, from) {
      this.getProductionReport();
    },
    active(to, from) {
      this.getTeam();
      this.getProductionReport();
    }
  }
};
</script>